import { useEffect, useRef, useState } from "react";
import BrandQR from "../components/BrandQR";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { CheckCircle2, Download, Calendar, MapPin } from "lucide-react";

export default function Confirmation() {
  const { registrationId } = useParams();
  const [data, setData] = useState(null);
  const qrWrapRef = useRef(null);

  useEffect(() => {
    api
      .get(`/registrations/${registrationId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [registrationId]);

  const download = () => {
    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `ticket-${registrationId}.png`;
    a.click();
  };

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );

  const { registration, event } = data;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 flex flex-col items-center">
      <div className="w-full max-w-md mt-6">
        <div className="flex items-center gap-2 mb-6">
          <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
          </span>
        </div>

        <div
          className="bg-white border border-zinc-200 rounded-md overflow-hidden"
          data-testid="ticket-card"
        >
          {/* Top: success */}
          <div className="p-6 border-b border-dashed border-zinc-300 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <div>
              <p className="overline text-green-700">Registration successful</p>
              <p className="text-sm text-zinc-500 mt-0.5">
                Show this QR at the event entrance.
              </p>
            </div>
          </div>

          {/* Save QR notice */}
          <div
            className="px-6 py-4 bg-amber-50 border-b border-dashed border-zinc-300"
            data-testid="save-qr-notice"
          >
            <p className="text-sm font-medium text-amber-900">
              Please save this QR code. It will be required for entry.
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              Tap <span className="font-semibold">Download ticket</span> below,
              or screenshot this screen and keep it handy.
            </p>
          </div>

          {/* QR */}
          <div className="p-8 flex flex-col items-center border-b border-dashed border-zinc-300">
            <div
              ref={qrWrapRef}
              className="p-4 border border-zinc-200 rounded-md bg-white"
              data-testid="qr-code-wrap"
            >
              <QRCodeCanvas
                value={registration.id}
                size={220}
                level="H"
                includeMargin={false}
              />
            </div>
            <p className="font-mono text-xs text-zinc-400 mt-4 break-all">
              {registration.id}
            </p>
          </div>

          {/* Event details */}
          <div className="p-6 space-y-3">
            <div>
              <p className="overline mb-1">Event</p>
              <p
                className="font-heading text-xl font-semibold tracking-tight"
                data-testid="ticket-event-name"
              >
                {event.name}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(event.date).toLocaleString([], {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </div>
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <MapPin className="w-3.5 h-3.5" />
                {event.location}
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-zinc-100 text-sm">
              <span className="text-zinc-500">Name</span>
              <span className="font-medium">{registration.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Sponsor</span>
              <span className="font-medium">{registration.sponsor_name}</span>
            </div>
          </div>
        </div>

        <Button
          onClick={download}
          variant="outline"
          className="w-full mt-6 h-11 rounded-md border-zinc-200"
          data-testid="download-ticket-button"
        >
          <Download className="w-4 h-4 mr-2" />
          Download ticket
        </Button>
      </div>
    </div>
  );
}
